import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface PhoneNumberProps {
  className?: string
}

/**
 * PhoneNumber component displays Little Sister's phone number
 * with tap-to-call and copy-to-clipboard functionality.
 */
export function PhoneNumber({ className }: PhoneNumberProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const phoneNumber = import.meta.env.VITE_LITTLE_SISTER_PHONE

  // Format phone number from +15551234567 to +1(555)-123-4567
  const formatPhoneNumber = (phone: string): string => {
    // Remove all non-digit characters except the leading +
    const cleaned = phone.replace(/[^\d+]/g, '')

    // Match +1 followed by 10 digits
    const match = cleaned.match(/^\+1(\d{3})(\d{3})(\d{4})$/)
    if (match) {
      return `+1(${match[1]})-${match[2]}-${match[3]}`
    }

    // Return original if it doesn't match expected format
    return phone
  }

  const formattedPhoneNumber = phoneNumber ? formatPhoneNumber(phoneNumber) : ''

  const handleCopy = async () => {
    if (!phoneNumber) return

    try {
      await navigator.clipboard.writeText(phoneNumber)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const containerStyles: React.CSSProperties = {
    backgroundColor: '#f0f7ff',
    borderRadius: '12px',
    padding: '1rem',
  }

  const labelStyles: React.CSSProperties = {
    fontSize: '14px',
    color: '#666',
    marginBottom: '0.5rem',
    fontWeight: 500,
  }

  const phoneRowStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  }

  const phoneLinkStyles: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#0066cc',
    textDecoration: 'none',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
  }

  const buttonStyles: React.CSSProperties = {
    minWidth: '44px',
    minHeight: '44px',
    padding: '0.5rem 1rem',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.25rem',
  }

  const copyButtonStyles: React.CSSProperties = {
    ...buttonStyles,
    backgroundColor: copied ? '#4ade80' : '#e5e7eb',
    color: copied ? '#166534' : '#374151',
  }

  const callButtonStyles: React.CSSProperties = {
    ...buttonStyles,
    backgroundColor: '#0066cc',
    color: '#fff',
  }

  const comingSoonStyles: React.CSSProperties = {
    fontSize: '16px',
    color: '#666',
    fontStyle: 'italic',
  }

  if (!phoneNumber) {
    return (
      <div style={containerStyles} className={className}>
        <div style={labelStyles}>{t('dashboard.littleSisterPhone')}</div>
        <div style={comingSoonStyles}>{t('dashboard.phoneNotConfigured')}</div>
      </div>
    )
  }

  return (
    <div style={containerStyles} className={className}>
      <div style={labelStyles}>{t('dashboard.littleSisterPhone')}</div>
      <div style={phoneRowStyles}>
        <a href={`tel:${phoneNumber}`} style={phoneLinkStyles}>
          {formattedPhoneNumber}
        </a>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={handleCopy}
            style={copyButtonStyles}
            aria-label={copied ? t('dashboard.copied') : t('dashboard.copyNumber')}
          >
            {copied ? (
              <>
                <span>✓</span> {t('dashboard.copied')}
              </>
            ) : (
              <>
                <span>📋</span> {t('dashboard.copyNumber')}
              </>
            )}
          </button>
          <a href={`tel:${phoneNumber}`} style={callButtonStyles}>
            📞 {t('dashboard.callNow')}
          </a>
        </div>
      </div>
    </div>
  )
}
